if (!customElements.get('quick-add-variant-modal')) {
  customElements.define(
    'quick-add-variant-modal',
    class QuickAddVariantModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddVariantInfo-"]');
        this.selectedVariantId = null;
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const handle = this.dataset.productHandle;

        fetch(`/products/${handle}.js`)
          .then((res) => res.json())
          .then((product) => {
            this.selectedVariantId = product.variants[0].id;
            this.classList.toggle('has-3-variants', product.variants.length >= 3);
            product._basePrice = Math.min(...product.variants.map((v) => v.price));
            this.modalContent.innerHTML = this.renderModal(product);
            this.bindEvents(product);
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // ─── Rendering ────────────────────────────────────────────────

      renderModal(product) {
        const variantData = this.parseVariantMetafields();

        const cards = product.variants
          .map((v, i) => this.renderCard(v, variantData[v.id] || {}, i === 0, product._basePrice))
          .join('');

        const colClass = product.variants.length >= 3 ? 'qav-cards--3col' : 'qav-cards--2col';

        const compareAt = product.variants[0].compare_at_price;
        const priceHTML = compareAt && compareAt > product.variants[0].price
          ? `<span class="qav-price-compare" id="qav-compare-price-${product.id}">${this.formatMoney(compareAt)}</span>
             <span class="qav-price-current" id="qav-selected-price-${product.id}">${this.formatMoney(product.variants[0].price)}</span>`
          : `<span class="qav-price-compare" id="qav-compare-price-${product.id}" style="display:none"></span>
             <span class="qav-price-current" id="qav-selected-price-${product.id}">${this.formatMoney(product.variants[0].price)}</span>`;

        return `
          <div class="qav-modal">
            <div class="qav-header">
              <div class="qav-header__image">
                ${product.featured_image
                  ? `<img src="${product.featured_image}" alt="${product.title}" width="72" height="72">`
                  : ''}
              </div>
              <div class="qav-header__info">
                <h2 class="qav-title">${product.title}</h2>
                <div class="qav-price-row">${priceHTML}</div>
              </div>
            </div>

            <p class="qav-choose-label">Choose your exam option:</p>

            <div class="qav-cards ${colClass}" role="radiogroup" aria-label="Variant options">
              ${cards}
            </div>

            <div class="qav-trust">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>${this.getTrustLine()}</span>
            </div>

            <button class="qav-atc button button--full-width" data-product-id="${product.id}">
              ${window.theme?.strings?.addToCart || 'Add to cart'}
            </button>

            <p class="qav-help">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              Not sure which to choose?
              <a href="${product.url}" class="qav-help__link">View full details</a>
            </p>
          </div>
        `;
      }

      renderCard(variant, meta, isSelected, basePrice) {
        const bullets = (meta.bullet_points || this.defaultBullets(variant))
          .map((b) => {
            const isNegative = /^no\s/i.test(b.trim());
            const icon = isNegative
              ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                   <line x1="18" y1="6" x2="6" y2="18"></line>
                   <line x1="6" y1="6" x2="18" y2="18"></line>
                 </svg>`
              : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                   <polyline points="20 6 9 17 4 12"></polyline>
                 </svg>`;
            return `
              <li class="qav-card__bullet ${isNegative ? 'qav-card__bullet--negative' : ''}">
                ${icon}
                ${b}
              </li>`;
          })
          .join('');

        const guidance = meta.experience_guidance || this.defaultGuidance(variant);

        const delta = variant.price - basePrice;
        const deltaHTML = delta > 0
          ? `<span class="qav-card__price-delta">+${this.formatMoney(delta)}</span>`
          : '';

        const isMostPopular = meta.is_most_popular ?? variant.title.toLowerCase().includes('take 2');
        const badgeLabel = meta.option_heading || 'Most Popular';

        return `
          <div
            class="qav-card ${isSelected ? 'qav-card--selected' : ''} ${isMostPopular ? 'qav-card--popular' : ''}"
            role="radio"
            aria-checked="${isSelected}"
            tabindex="0"
            data-variant-id="${variant.id}"
            data-price="${variant.price}"
            data-compare-at-price="${variant.compare_at_price || ''}"
          >
            ${isMostPopular ? `
              <div class="qav-card__badge">
                <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 9.2947C10.9352 9.50307 10.8193 9.69735 10.6102 9.7993C10.575 9.81645 10.401 9.88001 10.3767 9.88001H9.65562L9.89286 9.96884C10.5072 10.3223 10.3212 11.1837 9.61633 11.2822C8.38628 11.2284 7.07575 11.363 5.85497 11.2845C5.10611 11.2363 4.38305 10.8925 3.67046 10.6962L3.67782 5.61756C5.22119 5.23016 5.67121 3.59895 6.06603 2.30951C6.2339 2.03707 6.75466 2.11643 7.01535 2.21287C7.85753 2.52443 7.749 3.54442 7.6478 4.246C7.57771 4.73197 7.41169 5.20054 7.2602 5.66579H10.3767C10.401 5.66579 10.575 5.72935 10.6102 5.74649C10.8193 5.84844 10.9351 6.04272 11 6.2511V6.48522C10.9184 6.77733 10.7102 7.0076 10.3888 7.05906L10.6261 7.15937C10.8282 7.27342 10.9349 7.44416 11 7.65584V7.88996C10.9184 8.18207 10.7102 8.41234 10.3888 8.4638L10.6261 8.56411C10.8282 8.67816 10.9349 8.8489 11 9.06058L11 9.2947Z" fill="white"/>
                  <path d="M6.69801 0C6.82019 0.0595248 6.93443 0.13395 6.96121 0.274675C6.9899 0.425438 6.9861 0.951372 6.96691 1.11211C6.91658 1.53362 6.28389 1.5336 6.23357 1.11211C6.21437 0.951372 6.21058 0.425438 6.23926 0.274675C6.26604 0.13395 6.38028 0.0595225 6.50247 0H6.69801Z" fill="white"/>
                  <path d="M0.287208 4.9687C1.26936 5.00801 2.84167 4.63256 2.93493 6.00415C3.04512 7.62463 2.85033 9.35937 2.93339 10.9917C2.7653 12.3229 1.29363 11.9321 0.307626 11.9857C0.147242 11.9459 0.0556631 11.8637 0.00611211 11.7126L0 5.27891C0.0298403 5.1162 0.124638 5.01676 0.287208 4.9687ZM2.09447 9.98154C1.755 9.65639 1.23371 10.1557 1.57319 10.4808C1.91267 10.806 2.43395 10.3067 2.09447 9.98154Z" fill="white"/>
                  <path d="M4.14882 0.96484C4.20629 0.952477 4.32555 0.95247 4.38301 0.964849C4.50921 0.992037 5.08037 1.55319 5.12833 1.67889C5.24462 1.98373 4.92219 2.25038 4.61258 2.10285C4.49767 2.0481 3.94182 1.51869 3.91666 1.41154C3.87613 1.23897 3.95171 1.00724 4.14882 0.96484Z" fill="white"/>
                  <path d="M8.81746 0.964849C9.13195 0.897065 9.3713 1.12739 9.27784 1.42918C9.23466 1.5686 8.61018 2.14184 8.44284 2.15297C8.16058 2.17174 7.96899 1.89322 8.08707 1.64651C8.14158 1.53262 8.70135 0.989874 8.81746 0.964849Z" fill="white"/>
                </svg>
                MOST POPULAR
              </div>
            ` : ''}
            <div class="qav-card__header">
              <div class="qav-card__radio">
                <span class="qav-card__radio-dot"></span>
              </div>
              <span class="qav-card__title">${variant.title}</span>
              ${!isMostPopular ? `
                <div class="qav-card__price-wrap">
                  <span class="qav-card__price">${this.formatMoney(variant.price)}</span>
                  ${deltaHTML}
                </div>
              ` : ''}
            </div>
            ${isMostPopular ? `
              <div class="qav-card__bullets-row">
                <ul class="qav-card__bullets">${bullets}</ul>
                <div class="qav-card__price-wrap">
                  <span class="qav-card__price">${this.formatMoney(variant.price)}</span>
                  ${deltaHTML}
                </div>
              </div>
            ` : `
              <ul class="qav-card__bullets">${bullets}</ul>
            `}
            ${guidance ? `<p class="qav-card__guidance">${guidance}</p>` : ''}
          </div>
        `;
      }

      // ─── Events ───────────────────────────────────────────────────

      bindEvents(product) {
        const cards = this.modalContent.querySelectorAll('.qav-card');
        const priceEl = this.modalContent.querySelector(`#qav-selected-price-${product.id}`);
        const compareEl = this.modalContent.querySelector(`#qav-compare-price-${product.id}`);
        const atcBtn = this.modalContent.querySelector('.qav-atc');

        cards.forEach((card) => {
          const select = () => {
            cards.forEach((c) => {
              c.classList.remove('qav-card--selected');
              c.setAttribute('aria-checked', 'false');
            });
            card.classList.add('qav-card--selected');
            card.setAttribute('aria-checked', 'true');
            this.selectedVariantId = parseInt(card.dataset.variantId);

            if (priceEl) priceEl.textContent = this.formatMoney(parseInt(card.dataset.price));

            if (compareEl) {
              const cap = card.dataset.compareAtPrice;
              if (cap && parseInt(cap) > parseInt(card.dataset.price)) {
                compareEl.textContent = this.formatMoney(parseInt(cap));
                compareEl.style.display = '';
              } else {
                compareEl.textContent = '';
                compareEl.style.display = 'none';
              }
            }
          };

          card.addEventListener('click', select);
          card.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              select();
            }
          });
        });

        atcBtn.addEventListener('click', () => this.addToCart(atcBtn));
      }

      addToCart(btn) {
        if (!this.selectedVariantId) return;
        btn.setAttribute('disabled', true);
        btn.classList.add('loading');

        const drawer = document.querySelector('cart-drawer');
        const sections = drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map(s => s.id)
          : ['cart-drawer', 'cart-icon-bubble'];

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.selectedVariantId,
            quantity: 1,
            sections: sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((res) => res.json())
          .then((parsedState) => {
            this.hide();
            const drawer = document.querySelector('cart-drawer');
            if (drawer?.renderContents) drawer.renderContents(parsedState);
          })
          .catch((err) => console.error('QuickAddVariant: add to cart failed', err))
          .finally(() => {
            btn.removeAttribute('disabled');
            btn.classList.remove('loading');
          });
      }

      // ─── Helpers ──────────────────────────────────────────────────

      parseVariantMetafields() {
        try {
          const raw = this.dataset.variantDisplay;
          return raw ? JSON.parse(raw) : {};
        } catch {
          return {};
        }
      }

      defaultBullets(variant) {
        const t = variant.title.toLowerCase();
        if (t.includes('take 2')) {
          return [
            '2 exam attempts (free retake)',
            'Lower risk with second chance',
            'Recommended for first-time test takers',
          ];
        }
        if (t.includes('practice') && t.includes('pass')) {
          return [
            'Full practice exam access',
            'Pass guarantee included',
            'Retake support if needed',
          ];
        }
        if (t.includes('practice')) {
          return [
            'Realistic exam simulations',
            'Performance analytics',
            'No pass guarantee',
          ];
        }
        return [
          '1 official exam attempt',
          'Valid for 6 months',
          'No free retake',
        ];
      }

      defaultGuidance(variant) {
        const t = variant.title.toLowerCase();
        if (t.includes('take 2')) return 'Recommended for first-time test takers or anyone who wants added peace of mind.';
        if (t.includes('practice') && t.includes('pass')) return 'Ideal for candidates who want maximum confidence and pass protection.';
        if (t.includes('practice')) return 'Best for learners who want structured exam preparation.';
        return 'Best for experienced test takers who feel confident in passing on the first attempt.';
      }

      getTrustLine() {
        try {
          const raw = this.dataset.variantDisplay;
          if (raw) {
            const data = JSON.parse(raw);
            const first = Object.values(data)[0];
            if (first?.trust_line) return first.trust_line;
          }
        } catch {}
        return 'Instant email delivery.';
      }

      formatMoney(cents) {
        return (cents / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        });
      }
    }
  );
}